import _ from 'underscore';

import {BaseMigrationObjectPlan} from 'config-migration/types/MigrationObjects';
import {DestinationObjectIdentifier, ImportObjectIdentifier} from 'config-migration/types/MigrationObjects';
import {ObjectAction, ObjectActionType, UpdateAction} from 'config-migration/types/objectActions';
import {count} from 'metrics/Metrics';

export function isInaccessibleUpdateAction(action: ObjectAction): boolean {
  return action.type === ObjectActionType.UPDATE && action.target.objectInaccessible === true;
}

function normalizeAction(action: ObjectAction, allActions: Array<ObjectAction>): ObjectAction | null {
  if (isInaccessibleUpdateAction(action)) {
    if (allActions.length > 0 && allActions.every((a) => isInaccessibleUpdateAction(a))) {
      return {type: ObjectActionType.IGNORE, reason: 'no access'} as ObjectAction & {reason: string};
    }
    return null;
  }
  return action;
}

export default function convertImportIdentifiersToPlans(
  importIdentifiers: Array<ImportObjectIdentifier>,
  destinationIdentifiers: Array<DestinationObjectIdentifier>,
  shouldMatchByName: boolean,
  provenanceUriToPreviouslySelectedAction: Map<string, ObjectAction> = new Map()
): Array<BaseMigrationObjectPlan> {
  const importProvenanceUriToInitialAction = getImportProvenanceUriToInitialAction(
    importIdentifiers,
    destinationIdentifiers,
    shouldMatchByName
  );

  const importProvenanceUriToSelectedAction = getImportProvenanceUriToSelectedAction(
    importIdentifiers,
    provenanceUriToPreviouslySelectedAction,
    importProvenanceUriToInitialAction
  );

  // Normalize initial and selected actions and build ignored provenance URIs set
  const normalizedInitialActions = new Map<string, ObjectAction>();
  const normalizedSelectedActions = new Map<string, ObjectAction>();
  const ignoredProvenanceUris = new Set<string>();
  for (const importIdentifier of importIdentifiers) {
    const initialAction = importProvenanceUriToInitialAction.get(importIdentifier.provenanceUri)!;
    const selectedAction = importProvenanceUriToSelectedAction.get(importIdentifier.provenanceUri)!;
    // For initial/selected, treat as a single-action list
    const normalizedInitial = normalizeAction(initialAction, [initialAction]);
    const normalizedSelected = normalizeAction(selectedAction, [selectedAction]);
    if (normalizedInitial) {
      normalizedInitialActions.set(importIdentifier.provenanceUri, normalizedInitial);
      if (normalizedInitial.type === ObjectActionType.IGNORE) {
        ignoredProvenanceUris.add(importIdentifier.provenanceUri);
      }
    }
    if (normalizedSelected) {
      normalizedSelectedActions.set(importIdentifier.provenanceUri, normalizedSelected);
    }
  }

  // Pass ignoredProvenanceUris to getImportProvenanceUriToAdditionalActions
  const importProvenanceUriToAdditionalActions = getImportProvenanceUriToAdditionalActions(
    importIdentifiers,
    destinationIdentifiers,
    normalizedSelectedActions,
    ignoredProvenanceUris
  );

  return importIdentifiers.map((importIdentifier): BaseMigrationObjectPlan => {
    const initialAction = normalizedInitialActions.get(importIdentifier.provenanceUri)!;
    const selectedAction = normalizedSelectedActions.get(importIdentifier.provenanceUri)!;
    const additionalActions = importProvenanceUriToAdditionalActions.get(importIdentifier.provenanceUri)!;
    // Normalize and filter additional actions to only include valid UPDATE actions
    const normalizedAdditionalActions = additionalActions.filter(
      (action): action is UpdateAction =>
        action.type === ObjectActionType.UPDATE &&
        action.target != null &&
        !isInaccessibleUpdateAction(action)
    );
    const hasProvenanceUriMatch =
      initialAction.type === ObjectActionType.IGNORE ||
      (initialAction.type === ObjectActionType.UPDATE &&
        importIdentifier.provenanceUri === initialAction.target.provenanceUri);
    return {
      objectId: importIdentifier.provenanceUri,
      objectDisplayName: importIdentifier.displayName,
      initialAction,
      selectedAction,
      additionalActions: normalizedAdditionalActions,
      hasProvenanceUriMatch,
    };
  });
}

/**
 * Helper function that tries to match each import identifier to its initial action in the following steps:
 * 1) Find matching destination identifier by provenance URI
 * 2) If not found (and shouldMatchByName is true), match by exact name match
 * 3) If not found, default initial action to a "Create"
 *
 * Exported for testing.
 */
export function getImportProvenanceUriToInitialAction(
  importIdentifiers: Array<ImportObjectIdentifier>,
  destinationIdentifiers: Array<DestinationObjectIdentifier>,
  shouldMatchByName: boolean
): Map<string, ObjectAction> {
  const {importProvenanceUriToDestinationIdentifier: provenanceMatches} = matchImportToDestinationIdentifier(
    importIdentifiers,
    destinationIdentifiers,
    (importIdentifier) => importIdentifier.provenanceUri,
    (destinationIdentifier) => destinationIdentifier.provenanceUri
  );
  const importProvenanceUriToInitialAction = convertDestinationIdentifierToUpdateAction(provenanceMatches);

  if (shouldMatchByName) {
    const {importProvenanceUriToDestinationIdentifier: nameMatches} = matchImportToDestinationIdentifier(
      importIdentifiers,
      destinationIdentifiers,
      (importIdentifier) => importIdentifier.displayName,
      (destinationIdentifier) => destinationIdentifier.displayName
    );
    emitProvenanceAndNameMatchComparisonMetrics(provenanceMatches, nameMatches);

    const nameMatchUpdateActions = convertDestinationIdentifierToUpdateAction(nameMatches);
    for (const [importProvenanceUri, nameMatchAction] of nameMatchUpdateActions) {
      if (!importProvenanceUriToInitialAction.has(importProvenanceUri)) {
        importProvenanceUriToInitialAction.set(importProvenanceUri, nameMatchAction);
      }
    }
  }

  for (const importIdentifier of importIdentifiers) {
    if (!importProvenanceUriToInitialAction.has(importIdentifier.provenanceUri)) {
      importProvenanceUriToInitialAction.set(importIdentifier.provenanceUri, {
        type: ObjectActionType.CREATE,
      });
    }
  }
  return importProvenanceUriToInitialAction;
}

function emitProvenanceAndNameMatchComparisonMetrics(
  provenanceMatches: Map<string, DestinationObjectIdentifier>,
  nameMatches: Map<string, DestinationObjectIdentifier>
): void {
  // Hack to infer the object type based on the api id of the first object
  let firstObject = null;
  if (provenanceMatches.size > 0) {
    firstObject = provenanceMatches.values().next().value;
  } else {
    firstObject = nameMatches.values().next().value;
  }
  const objectType = firstObject?.apiIdentifier.split('_')[0] ?? '';

  let nameAndProvenanceAgree = 0;
  let nameMatchOnly = 0;
  let provenanceMatchOnly = 0;
  let nameAndProvenanceDisagree = 0;
  for (const [provenanceUri, destinationIdentifier] of provenanceMatches.entries()) {
    const nameMatch = nameMatches.get(provenanceUri);
    if (nameMatch) {
      if (nameMatch.apiIdentifier === destinationIdentifier.apiIdentifier) {
        nameAndProvenanceAgree++;
      } else {
        nameAndProvenanceDisagree++;
      }
    } else {
      provenanceMatchOnly++;
    }
  }
  for (const provenanceUri of nameMatches.keys()) {
    if (!provenanceMatches.has(provenanceUri)) {
      nameMatchOnly++;
    }
  }
  const matchTypeToCount = new Map<string, number>([
    ['NAME_AND_PROVENANCE_AGREE', nameAndProvenanceAgree],
    ['NAME_MATCH_ONLY', nameMatchOnly],
    ['PROVENANCE_MATCH_ONLY', provenanceMatchOnly],
    ['NAME_AND_PROVENANCE_DISAGREE', nameAndProvenanceDisagree],
  ]);
  for (const [matchType, occurrences] of matchTypeToCount.entries()) {
    if (occurrences > 0) {
      count('bnch.config_migration.name_and_provenance_match', occurrences, {
        matchType,
        objectType,
      });
    }
  }
}

function matchImportToDestinationIdentifier(
  importIdentifiers: Array<ImportObjectIdentifier>,
  destinationIdentifiers: Array<DestinationObjectIdentifier>,
  convertImportIdentifierToMatchValue: (importIdentifier: ImportObjectIdentifier) => string,
  convertDestinationIdentifierToMatchValue: (destinationIdentifier: DestinationObjectIdentifier) => string,
  extraMatchedProvenanceUris: Set<string> = new Set()
): {
  importProvenanceUriToDestinationIdentifier: Map<string, DestinationObjectIdentifier>;
  unmatchedImportIdentifiers: Array<ImportObjectIdentifier>;
  unmatchedDestinationIdentifiers: Array<DestinationObjectIdentifier>;
} {
  const importProvenanceUriToDestinationIdentifier = new Map<string, DestinationObjectIdentifier>();
  const unmatchedImportIdentifiers: Array<ImportObjectIdentifier> = [];
  const matchedDestinationIdentifiers = new Set<DestinationObjectIdentifier>();

  const matchPropertyToDestinationIdentifier = _.indexBy(
    destinationIdentifiers,
    convertDestinationIdentifierToMatchValue
  );

  for (const importIdentifier of importIdentifiers) {
    const match = matchPropertyToDestinationIdentifier[convertImportIdentifierToMatchValue(importIdentifier)];
    if (match) {
      importProvenanceUriToDestinationIdentifier.set(importIdentifier.provenanceUri, match);
      matchedDestinationIdentifiers.add(match);
    } else {
      unmatchedImportIdentifiers.push(importIdentifier);
    }
  }

  // Add extra matched provenance URIs (for IGNORED inaccessible objects)
  const unmatchedDestinationIdentifiers = destinationIdentifiers.filter(
    (destinationIdentifier) =>
      !matchedDestinationIdentifiers.has(destinationIdentifier) &&
      !extraMatchedProvenanceUris.has(destinationIdentifier.provenanceUri)
  );
  return {
    importProvenanceUriToDestinationIdentifier,
    unmatchedImportIdentifiers,
    unmatchedDestinationIdentifiers,
  };
}

function convertDestinationIdentifierToUpdateAction(
  importProvenanceUriToDestinationIdentifier: Map<string, DestinationObjectIdentifier>
): Map<string, ObjectAction> {
  return Array.from(importProvenanceUriToDestinationIdentifier.keys()).reduce((map, importProvenanceUri) => {
    map.set(importProvenanceUri, {
      type: ObjectActionType.UPDATE,
      target: importProvenanceUriToDestinationIdentifier.get(importProvenanceUri)!,
    });
    return map;
  }, new Map<string, ObjectAction>());
}

/**
 * Helper function that gets the selected action for each import identifier.
 * If the import object has a previously selected action, use that.
 * Else, default to the initial action.
 *
 * Exported for testing.
 */
export function getImportProvenanceUriToSelectedAction(
  importIdentifiers: Array<ImportObjectIdentifier>,
  provenanceUriToPreviouslySelectedAction: Map<string, ObjectAction>,
  importProvenanceUriToInitialAction: Map<string, ObjectAction>
): Map<string, ObjectAction> {
  return importIdentifiers.reduce((map, importIdentifier) => {
    const previouslySelectedAction = provenanceUriToPreviouslySelectedAction.get(
      importIdentifier.provenanceUri
    );
    const initialAction = importProvenanceUriToInitialAction.get(importIdentifier.provenanceUri)!;
    map.set(importIdentifier.provenanceUri, previouslySelectedAction ?? initialAction);
    return map;
  }, new Map<string, ObjectAction>());
}

/**
 * Helper function that, for each import object,
 * returns an additional Update action per destination object that is not matched by provenance URI or selected elsewhere,
 * with name matched destination objects first and the rest in alphabetical order.
 *
 * Exported for testing.
 */
export function getImportProvenanceUriToAdditionalActions(
  importIdentifiers: Array<ImportObjectIdentifier>,
  destinationIdentifiers: Array<DestinationObjectIdentifier>,
  importProvenanceUriToSelectedAction: Map<string, ObjectAction>,
  ignoredProvenanceUris: Set<string> = new Set()
): Map<string, Array<UpdateAction>> {
  const importProvenanceUris = new Set(
    importIdentifiers.map((importIdentifier) => importIdentifier.provenanceUri)
  );
  // Filter out ignored provenance URIs from destinationIdentifiers
  const destinationIdentifiersWithoutProvenanceUriMatch = destinationIdentifiers.filter(
    (destinationIdentifier) =>
      !importProvenanceUris.has(destinationIdentifier.provenanceUri) &&
      !ignoredProvenanceUris.has(destinationIdentifier.provenanceUri)
  );
  const allSelectedActions = Array.from(importProvenanceUriToSelectedAction.values());

  return importIdentifiers.reduce((map, importIdentifier) => {
    const selectedAction = importProvenanceUriToSelectedAction.get(importIdentifier.provenanceUri)!;
    const additionalActions = getAdditionalActions(
      importIdentifier,
      destinationIdentifiersWithoutProvenanceUriMatch
    ).filter((action) => shouldKeepAdditionalAction(action, allSelectedActions, selectedAction));
    map.set(importIdentifier.provenanceUri, additionalActions);
    return map;
  }, new Map<string, Array<UpdateAction>>());
}

function getAdditionalActions(
  importIdentifier: ImportObjectIdentifier,
  destinationIdentifiers: Array<DestinationObjectIdentifier>
): Array<UpdateAction> {
  const [matchedByName, otherIdentifiers] = _.partition(
    destinationIdentifiers,
    (destinationIdentifier) => destinationIdentifier.displayName === importIdentifier.displayName
  );
  const orderedDestinationIdentifiers = [
    ...matchedByName,
    ..._.sortBy(
      _.sortBy(otherIdentifiers, (identifier) => identifier.displayName.toLowerCase()),
      (identifier) => identifier.archived
    ),
  ];
  return orderedDestinationIdentifiers.map(
    (destinationIdentifier): UpdateAction => ({
      type: ObjectActionType.UPDATE,
      target: destinationIdentifier,
    })
  );
}

/**
 * Keep the additionalAction if it has not been selected for matching for a different object.
 *
 * Exported for testing.
 */
export function shouldKeepAdditionalAction(
  additionalAction: UpdateAction,
  allSelectedActions: Array<ObjectAction>,
  currentlySelectedAction: ObjectAction
): boolean {
  return (
    areActionsEqual(currentlySelectedAction, additionalAction) ||
    !allSelectedActions.some((action) => areActionsEqual(action, additionalAction))
  );
}

export function areActionsEqual(action1: ObjectAction, action2: ObjectAction): boolean {
  if (action1.type === ObjectActionType.UPDATE && action2.type === ObjectActionType.UPDATE) {
    return action1.target.apiIdentifier === action2.target.apiIdentifier;
  }
  return action1.type === action2.type;
}
